import { Capacitor } from '@capacitor/core'
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import api, { authAPI } from '../services/api'
import { authStorage } from '../lib/authStorage'
import { NATIVE_PICKER_OFF } from '../lib/googleSignIn'

interface User {
  id: string
  username: string
  email: string
  first_name: string
  last_name: string
  role: string
  program?: string
  year_level?: string
  profile_picture?: string | null
}

interface AuthContextType {
  user: User | null
  token: string | null
  loading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setAuthData: (token: string, user: User, refresh?: string) => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * True from the moment a deliberate sign-out starts until the tab has left for a
 * fresh document.
 *
 * `logout()` clears the session and then assigns `window.location.href`. That
 * assignment starts a navigation but does not stop JavaScript: React re-renders
 * first, and every mounted `ProtectedRoute` sees a missing token. From inside
 * the guard, "the user just signed out" and "someone deep-linked to a page they
 * must sign in for" are the same condition, so the guard recorded the page it
 * was on as the place to return to after login — putting `loginReturnUrl` back
 * moments after logout had wiped it.
 *
 * The next person to sign in on that tab was then sent to the previous
 * account's panel. Because an admin is allowed through every route, an admin
 * landing on the student's saved URL rendered the student dashboard, which reads
 * exactly like a logout that did not work.
 *
 * Module-level, not state: the guard has to see this during the very same
 * synchronous render pass that the logout triggers, before any effect could run.
 * It is never reset — the only way out of this state is the pending document
 * load, which discards the flag with the rest of the page.
 */
let signingOut = false

export const isSigningOut = () => signingOut

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Derived state for authentication status
  const isAuthenticated = Boolean(token && user)

  // Restore the session. Where it lives depends on the platform — see
  // lib/authStorage: per-tab on the web so two roles in two tabs cannot mix,
  // persistent in the app because a WebView has one tab and Android throws
  // sessionStorage away with the process.
  useEffect(() => {
    let storedToken = authStorage.get('token')
    let storedUser = authStorage.get('user')

    // Nothing here? A session may still be sitting in the store this platform
    // used to use, and both directions happen for real:
    //
    //   web     older builds wrote to localStorage before the session became
    //           per-tab.
    //   app     builds before the session moved to localStorage wrote to
    //           sessionStorage. Without this, updating the APK signs everyone
    //           out once — which is the very complaint that prompted the move.
    //
    // Whatever is found is copied into the current store and removed from the
    // old one, so the two can never disagree about who is signed in.
    if (!storedToken && !storedUser) {
      const previous = authStorage.persistent ? sessionStorage : localStorage
      const priorToken = previous.getItem('token')
      const priorUser = previous.getItem('user')
      const priorRefresh = previous.getItem('refresh_token')
      if (priorToken && priorUser) {
        storedToken = priorToken
        storedUser = priorUser
        authStorage.set('token', priorToken)
        authStorage.set('user', priorUser)
        if (priorRefresh) authStorage.set('refresh_token', priorRefresh)
      }
      previous.removeItem('token')
      previous.removeItem('user')
      previous.removeItem('refresh_token')
    }

    if (storedToken && storedUser) {
      try {
        setToken(storedToken)
        setUser(JSON.parse(storedUser))
        setLoading(false)
        return
      } catch (error) {
        // Unparseable is not the same as absent: this copy is corrupt, so drop
        // it and fall through to fetching a fresh one below.
        console.error('Failed to parse stored user:', error)
        authStorage.remove('user')
        storedUser = null
      }
    }

    /**
     * A token with no profile beside it is still a session.
     *
     * `isAuthenticated` is `token && user`, and this used to restore nothing
     * unless both were present — so losing just the cached profile presented
     * as being signed out, while the token sat untouched in storage. That is
     * exactly what the device reported: "signed in yes, token in localStorage
     * present", on the login screen.
     *
     * The token is the credential. The user object is a cache of /auth/profile
     * kept so the first paint has a name and a role. A cache going missing must
     * cost a request, not a session — whatever made it go missing, which for a
     * WebView that Android kills at will is not something this code can police.
     *
     * If the token is genuinely dead the request 401s, the interceptor tries a
     * refresh, and only if that also fails does the session end.
     */
    if (storedToken) {
      setToken(storedToken)
      api.get('/auth/profile/')
        .then(response => {
          setUser(response.data)
          authStorage.set('user', JSON.stringify(response.data))
        })
        .catch(() => {
          // The interceptor has already redirected if this was terminal.
          setToken(null)
        })
        .finally(() => setLoading(false))
      return
    }

    setLoading(false)
  }, [])

  const setAuthData = useCallback((newToken: string, newUser: User, refresh?: string) => {
    setToken(newToken)
    setUser(newUser)
    // One store, chosen by platform. See lib/authStorage.
    authStorage.set('token', newToken)
    authStorage.set('user', JSON.stringify(newUser))
    if (refresh) authStorage.set('refresh_token', refresh)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const response = await authAPI.login(email, password)
    const tokens = response.data.tokens || {
      access: response.data.access,
      refresh: response.data.refresh,
    }
    setAuthData(tokens.access, response.data.user, tokens.refresh)
  }, [setAuthData])

  const logout = useCallback(() => {
    // Before the state updates below, so the re-render they cause already knows
    // this is a teardown and not a blocked navigation. See `signingOut`.
    signingOut = true

    setToken(null)
    setUser(null)

    // Wipe the session, rather than a list of keys somebody remembered.
    //
    // This used to remove an allowlist — token, user, refresh, a few chat keys,
    // the AI mentor ids — so every key added afterwards survived a logout by
    // default. Two did, and one of them was visible:
    //
    //   `loginReturnUrl`  ProtectedRoute stores the page you were sent away
    //                     from, and Login redirects there after sign-in. An
    //                     instructor logging out on the lab monitor, then a
    //                     student signing in on the same tab, landed the
    //                     student on the instructor's monitor. It looked for
    //                     all the world like the logout had not worked.
    //
    //   `settings_verified`  a privilege flag. Surviving a logout means the
    //                        next person on that tab inherits it.
    //
    // Inverted, so the default is safe: everything goes unless it is named as
    // genuinely global.
    //
    // The native picker's off switch is global: it exists so a handset the
    // picker crashes on can be rescued without a release, and wiping it would
    // turn the crash back on at the next sign-in — exactly when it bites.
    const KEEP_ACROSS_LOGOUT = new Set<string>([NATIVE_PICKER_OFF])

    const wipe = (storage: Storage) => {
      const doomed: string[] = []
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i)
        if (key && !KEEP_ACROSS_LOGOUT.has(key)) doomed.push(key)
      }
      doomed.forEach(key => storage.removeItem(key))
    }
    wipe(sessionStorage)
    wipe(localStorage)

    // Redirect to login on mobile.
    //
    // Nothing is awaited here on purpose. This briefly asked Play services to
    // forget the Google account before leaving, so the next sign-in would
    // offer a picker — which does not solve the problem it was written for: a
    // sign-in the server *rejects* never reaches a sign-out, so the bad
    // account stayed cached and every retry resubmitted it. The picker is
    // cleared on the way in instead, every time. See lib/googleSignIn.
    if (Capacitor.isNativePlatform()) {
      window.location.href = '/login';
    } else {
      window.location.href = '/';
    }
  }, [])

  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await api.get('/auth/profile/')
        const updatedUser = response.data
        setUser(updatedUser)
        // One store. This wrote to both, which put the signed-in identity in
        // localStorage on the web — readable by every other tab, and the exact
        // thing the per-tab session exists to prevent.
        authStorage.set('user', JSON.stringify(updatedUser))
      } catch (error) {
        console.error('Failed to refresh user:', error)
      }
    }
  }, [token])

  return (
    <AuthContext.Provider value={{ user, token, loading, isAuthenticated, login, logout, setAuthData, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
